import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plus, DotsSixVertical, CurrencyDollar, CalendarBlank, User,
  Trash, PencilSimple, ArrowRight, FunnelSimple
} from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';
import { GhostBusterPanel } from '../components/CrunchyAgents';
import { DatePicker } from '../components/DateTimePickers';

const STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-gray-100 border-gray-300 text-gray-700' },
  { id: 'qualified', label: 'Qualified', color: 'bg-blue-50 border-blue-300 text-blue-700' },
  { id: 'proposal', label: 'Proposal', color: 'bg-amber-50 border-amber-300 text-amber-700' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-purple-50 border-purple-300 text-purple-700' },
  { id: 'won', label: 'Won', color: 'bg-green-50 border-green-300 text-green-700' },
  { id: 'lost', label: 'Lost', color: 'bg-red-50 border-red-300 text-red-700' },
];

function formatCurrency(val) {
  if (!val) return '$0';
  return '$' + Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function DealCard({ deal, onEdit }) {
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/plain', deal.deal_id)}
      className="bg-white border border-[#E5E7EB] rounded-sm p-3 hover:shadow-sm hover:-translate-y-[1px] transition-all cursor-grab active:cursor-grabbing group"
      onClick={() => onEdit(deal)}
      data-testid={`deal-card-${deal.deal_id}`}
    >
      <div className="flex items-start justify-between mb-1.5">
        <h4 className="text-sm font-medium text-[#111827] leading-tight pr-2">{deal.title}</h4>
      </div>
      {deal.value > 0 && (
        <div className="flex items-center gap-1 text-sm font-bold text-[#002FA7] mb-1.5">
          <CurrencyDollar size={14} weight="bold" />
          {formatCurrency(deal.value)}
        </div>
      )}
      {deal.close_date && (
        <div className="flex items-center gap-1 text-xs text-[#4B5563] mb-1.5">
          <CalendarBlank size={12} />
          {deal.close_date}
        </div>
      )}
      {deal.contact_name && (
        <div className="flex items-center gap-1 text-xs text-[#4B5563]">
          <User size={12} />
          {deal.contact_name}
        </div>
      )}
    </div>
  );
}

export default function PipelinePage() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [form, setForm] = useState({
    title: '', value: '', stage: 'lead', close_date: '', notes: '', contact_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchData = async () => {
    try {
      const [dealsRes, summaryRes] = await Promise.all([
        api.get('/deals'),
        api.get('/deals/summary'),
      ]);

      // Enrich deals with contact names
      const enriched = await Promise.all(
        dealsRes.data.map(async (deal) => {
          if (deal.contact_id) {
            try {
              const { data } = await api.get(`/contacts/${deal.contact_id}`);
              return { ...deal, contact_name: `${data.first_name} ${data.last_name}`.trim() };
            } catch {
              return deal;
            }
          }
          return deal;
        })
      );

      setDeals(enriched);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to load deals', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = (stage = 'lead') => {
    setEditingDeal(null);
    setForm({ title: '', value: '', stage, close_date: '', notes: '', contact_id: '' });
    setSelectedContact(null);
    setDialogOpen(true);
  };

  const openEdit = (deal) => {
    setEditingDeal(deal);
    setForm({
      title: deal.title, value: String(deal.value || ''), stage: deal.stage,
      close_date: deal.close_date || '', notes: deal.notes || '', contact_id: deal.contact_id || '',
    });
    setSelectedContact(deal.contact_name ? { name: deal.contact_name, contact_id: deal.contact_id } : null);
    setDialogOpen(true);
  };

  const searchContacts = async (q) => {
    setContactSearch(q);
    if (q.length < 2) { setContactResults([]); return; }
    try {
      const { data } = await api.get('/contacts/search/quick', { params: { q } });
      setContactResults(data);
    } catch { setContactResults([]); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: parseFloat(form.value) || 0,
        contact_id: form.contact_id || '',
      };
      if (editingDeal) {
        await api.put(`/deals/${editingDeal.deal_id}`, payload);
      } else {
        await api.post('/deals', payload);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingDeal || !window.confirm('Delete this deal?')) return;
    try {
      await api.delete(`/deals/${editingDeal.deal_id}`);
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const moveDeal = async (dealId, newStage) => {
    try {
      await api.put(`/deals/${dealId}`, { stage: newStage });
      fetchData();
    } catch (err) {
      console.error('Move failed', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pipeline-loading">
        <div className="w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const dealsByStage = {};
  STAGES.forEach(s => { dealsByStage[s.id] = deals.filter(d => d.stage === s.id); });

  const totalPipelineValue = deals
    .filter(d => !['won', 'lost'].includes(d.stage))
    .reduce((sum, d) => sum + (d.value || 0), 0);

  return (
    <div data-testid="pipeline-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#111827]" data-testid="pipeline-title">
            Sales Pipeline
          </h1>
          <p className="text-sm text-[#4B5563] mt-0.5">
            {deals.length} deals &middot; {formatCurrency(totalPipelineValue)} in pipeline
          </p>
          <WatchHowLink slug="pipeline" className="mt-1" />
        </div>
        <Button
          onClick={() => openCreate()}
          className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm gap-1.5"
          data-testid="add-deal-btn"
        >
          <Plus size={16} weight="bold" />
          Add Deal
        </Button>
      </div>

      {/* GhostBuster — Crunchy's cold-deal reactivator */}
      <GhostBusterPanel />

      {/* Kanban Board */}
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
        {STAGES.map((stage) => {
          const stageDeals = dealsByStage[stage.id] || [];
          const stageValue = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-64 bg-[#F9FAFB] border border-[#E5E7EB] rounded-sm flex flex-col"
              data-testid={`pipeline-column-${stage.id}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain');
                if (id) moveDeal(id, stage.id);
              }}
            >
              {/* Column Header */}
              <div className="px-3 py-2.5 border-b border-[#E5E7EB]">
                <div className="flex items-center justify-between mb-1">
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-sm border ${stage.color}`}>
                    {stage.label}
                  </span>
                  <span className="text-xs font-bold text-[#4B5563]">{stageDeals.length}</span>
                </div>
                {stageValue > 0 && (
                  <p className="text-xs text-[#4B5563]">{formatCurrency(stageValue)}</p>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 p-2 space-y-2 min-h-[200px] overflow-y-auto max-h-[60vh]">
                {stageDeals.map((deal) => (
                  <DealCard key={deal.deal_id} deal={deal} onEdit={openEdit} onMove={moveDeal} />
                ))}
                {stageDeals.length === 0 && (
                  <p className="text-xs text-[#4B5563] text-center py-8">No deals</p>
                )}
              </div>

              {/* Add button at bottom */}
              <button
                onClick={() => openCreate(stage.id)}
                className="flex items-center justify-center gap-1 px-3 py-2 border-t border-[#E5E7EB] text-xs text-[#4B5563] hover:text-[#002FA7] hover:bg-white transition-colors"
                data-testid={`add-deal-${stage.id}-btn`}
              >
                <Plus size={12} weight="bold" /> Add
              </button>
            </div>
          );
        })}
      </div>

      {/* Deal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px] rounded-sm border-[#E5E7EB] p-0 gap-0" data-testid="deal-dialog">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-base font-bold text-[#111827]">
              {editingDeal ? 'Edit Deal' : 'New Deal'}
            </DialogTitle>
          </DialogHeader>
          <div className="px-5 pb-4 space-y-3">
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Deal Title *</Label>
              <Input
                autoFocus
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Enterprise License Deal"
                className="mt-1 rounded-sm h-9 text-sm"
                data-testid="deal-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Value ($)</Label>
                <Input
                  type="number"
                  value={form.value}
                  onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
                  placeholder="10000"
                  className="mt-1 rounded-sm h-9 text-sm"
                  data-testid="deal-value-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#4B5563]">Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm(f => ({ ...f, stage: v }))}>
                  <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="deal-stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Close Date</Label>
              <div className="mt-1">
                <DatePicker
                  value={form.close_date}
                  onChange={(v) => setForm(f => ({ ...f, close_date: v }))}
                  testId="deal-close-date"
                  height="md"
                  placeholder="Pick close date"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Linked Contact</Label>
              {selectedContact ? (
                <div className="mt-1 flex items-center gap-2 p-2 bg-[#F3F4F6] border border-[#E5E7EB] rounded-sm">
                  <User size={14} className="text-[#4B5563]" />
                  <span className="text-sm font-medium text-[#111827] flex-1">{selectedContact.name || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}</span>
                  <button
                    onClick={() => { setSelectedContact(null); setForm(f => ({ ...f, contact_id: '' })); }}
                    className="text-xs text-[#DC2626] hover:underline"
                    data-testid="deal-unlink-contact"
                  >Remove</button>
                </div>
              ) : (
                <div className="mt-1">
                  <Input
                    value={contactSearch}
                    onChange={(e) => searchContacts(e.target.value)}
                    placeholder="Search contact..."
                    className="rounded-sm h-9 text-sm"
                    data-testid="deal-contact-search"
                  />
                  {contactSearch && contactSearch.length === 1 && (
                    <p className="mt-1 text-xs text-[#6B7280] italic" data-testid="deal-contact-hint">Keep typing — searching after 2 characters.</p>
                  )}
                  {contactSearch && contactSearch.length >= 2 && contactResults.length === 0 && (
                    <p className="mt-1 text-xs text-[#DC2626]" data-testid="deal-contact-no-results">
                      No contacts match "{contactSearch}".
                    </p>
                  )}
                  {contactResults.length > 0 && (
                    <div className="mt-1 border border-[#E5E7EB] rounded-sm bg-white max-h-32 overflow-y-auto">
                      {contactResults.map(c => (
                        <button
                          key={c.contact_id}
                          onClick={() => {
                            setSelectedContact(c);
                            setForm(f => ({ ...f, contact_id: c.contact_id }));
                            setContactResults([]);
                            setContactSearch('');
                          }}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#F3F4F6] transition-colors"
                          data-testid={`deal-contact-option-${c.contact_id}`}
                        >
                          <span className="font-medium">{c.first_name} {c.last_name}</span>
                          {c.company && <span className="text-xs text-[#4B5563] ml-1">({c.company})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="mt-1 rounded-sm text-sm"
                placeholder="Deal notes..."
                data-testid="deal-notes-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            {editingDeal ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="text-xs text-[#DC2626] hover:text-[#DC2626] hover:bg-red-50 rounded-sm gap-1"
                data-testid="delete-deal-btn"
              >
                <Trash size={14} /> Delete
              </Button>
            ) : <div />}
            <Button
              onClick={handleSave}
              disabled={saving || !form.title.trim()}
              className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm text-sm h-8 px-4"
              data-testid="save-deal-btn"
            >
              {saving ? 'Saving...' : editingDeal ? 'Update Deal' : 'Create Deal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
